import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, FileText, Loader2, Printer, ArrowLeft, StickyNote, X, Phone, Calendar, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const ApprovedList = ({ companies, onBack, currentUser, onDelete, onRefresh }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [detailedAnalysis, setDetailedAnalysis] = useState({});
  const [loadingAnalysis, setLoadingAnalysis] = useState(null);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [currentNotesCompany, setCurrentNotesCompany] = useState(null);
  const [notesText, setNotesText] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'recall'
  const [viewCompanyModal, setViewCompanyModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [editingDateCompanyId, setEditingDateCompanyId] = useState(null);
  const [tempDate, setTempDate] = useState('');
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [searchText, setSearchText] = useState('');

  const approvedCompanies = companies
    .filter(c => c.status === 'APPROVED')
    .sort((a, b) => {
      const dateA = a.approved_at ? new Date(a.approved_at) : new Date(a.created_at);
      const dateB = b.approved_at ? new Date(b.approved_at) : new Date(b.created_at);
      return dateB - dateA;
    });

  const searchLower = searchText.trim().toLowerCase();
  const matchesSearch = (c) =>
    !searchLower ||
    [c.title, c.city, c.website, c.address, c.contact_email, c.contact_phone, c.phone]
      .some(v => (v || '').toString().toLowerCase().includes(searchLower));

  // Split into two lists
  const normalApproved = approvedCompanies.filter(c => !c.needs_recall && matchesSearch(c));
  const recallList = approvedCompanies.filter(c => c.needs_recall && matchesSearch(c));

  const fetchDetailedAnalysis = async (companyId) => {
    if (detailedAnalysis[companyId]) {
      setExpandedId(expandedId === companyId ? null : companyId);
      return;
    }

    setLoadingAnalysis(companyId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/companies/${companyId}/detailed-analysis`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!response.ok) {
        throw new Error('Fehler beim Laden der detaillierten Analyse');
      }
      
      const data = await response.json();
      setDetailedAnalysis(prev => ({
        ...prev,
        [companyId]: data.detailed_analysis
      }));
      setExpandedId(companyId);
    } catch (error) {
      console.error('Detailed analysis failed:', error);
      toast.error('❌ Detaillierte Analyse fehlgeschlagen');
    } finally {
      setLoadingAnalysis(null);
    }
  };

  const handleRecallToggle = async (companyId, currentValue) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/companies/${companyId}/recall`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ needs_recall: !currentValue })
        }
      );

      if (response.ok) {
        toast.success(!currentValue ? '✅ Zum erneuten Anrufen markiert' : '✅ Markierung entfernt');
        onRefresh && onRefresh();
      } else {
        toast.error('❌ Fehler beim Aktualisieren');
      }
    } catch (error) {
      console.error('Recall toggle failed:', error);
      toast.error('❌ Fehler beim Aktualisieren');
    }
  };

  const openNotesModal = (company) => {
    setCurrentNotesCompany(company);
    setNotesText('');  // Always start with empty text for append-only behavior
    setNotesModalOpen(true);
  };

  const saveNotes = async () => {
    if (!currentNotesCompany || !notesText.trim()) return;

    setSavingNotes(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/companies/${currentNotesCompany.id}/notes`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ note: notesText })
        }
      );

      if (response.ok) {
        toast.success('✅ Notiz hinzugefügt');
        setNotesModalOpen(false);
        setNotesText('');
        onRefresh && onRefresh();
      } else {
        toast.error('❌ Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Save notes failed:', error);
      toast.error('❌ Fehler beim Speichern');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleDeleteNote = async (companyId, noteIndex) => {
    if (!window.confirm('Diese Notiz wirklich löschen?')) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/companies/${companyId}/notes/${noteIndex}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        toast.success('✅ Notiz gelöscht');
        
        // Refresh data
        if (onRefresh) {
          await onRefresh();
        }
        
        // Update selectedCompany if modal is open
        if (selectedCompany && selectedCompany.id === companyId) {
          // Wait a bit for refresh to complete
          setTimeout(() => {
            const updatedCompany = companies.find(c => c.id === companyId);
            if (updatedCompany) {
              setSelectedCompany(updatedCompany);
            }
          }, 500);
        }
      } else {
        toast.error('❌ Fehler beim Löschen');
      }
    } catch (error) {
      console.error('Delete note failed:', error);
      toast.error('❌ Fehler beim Löschen');
    }
  };

  const handleUpdateNote = async (companyId, noteIndex, newText) => {
    if (!newText.trim()) {
      toast.error('❌ Notiz darf nicht leer sein');
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/companies/${companyId}/notes/${noteIndex}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ note: newText })
        }
      );

      if (response.ok) {
        toast.success('✅ Notiz aktualisiert');
        setEditingNoteIndex(null);
        setEditingNoteText('');
        
        // Refresh data
        if (onRefresh) {
          await onRefresh();
        }
        
        // Update selectedCompany if modal is open
        if (selectedCompany && selectedCompany.id === companyId) {
          // Wait a bit for refresh to complete
          setTimeout(() => {
            const updatedCompany = companies.find(c => c.id === companyId);
            if (updatedCompany) {
              setSelectedCompany(updatedCompany);
            }
          }, 500);
        }
      } else {
        toast.error('❌ Fehler beim Aktualisieren');
      }
    } catch (error) {
      console.error('Update note failed:', error);
      toast.error('❌ Fehler beim Aktualisieren');
    }
  };

  return (
    <div>
      {/* Header */}
      <Card className="mb-6 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={onBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Zurück
              </Button>
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  Genehmigte Unternehmen ({approvedCompanies.length})
                </CardTitle>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const res = await fetch(
                      `${process.env.REACT_APP_BACKEND_URL}/api/companies/export?status=APPROVED&format=xlsx`,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    if (!res.ok) throw new Error('Export fehlgeschlagen');
                    const blob = await res.blob();
                    const disp = res.headers.get('content-disposition') || '';
                    const match = disp.match(/filename="?([^";]+)"?/);
                    const filename = match ? match[1] : `approved.xlsx`;
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success('Excel heruntergeladen');
                  } catch (e) {
                    toast.error('Excel Export fehlgeschlagen');
                  }
                }}
                variant="outline"
                size="sm"
                data-testid="approved-export-xlsx-btn"
              >
                📊 Excel
              </Button>
              <Button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const res = await fetch(
                      `${process.env.REACT_APP_BACKEND_URL}/api/companies/export?status=APPROVED&format=csv`,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    if (!res.ok) throw new Error('Export fehlgeschlagen');
                    const blob = await res.blob();
                    const disp = res.headers.get('content-disposition') || '';
                    const match = disp.match(/filename="?([^";]+)"?/);
                    const filename = match ? match[1] : `approved.csv`;
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success('CSV heruntergeladen');
                  } catch (e) {
                    toast.error('CSV Export fehlgeschlagen');
                  }
                }}
                variant="outline"
                size="sm"
                data-testid="approved-export-csv-btn"
              >
                📄 CSV
              </Button>
              <Button
                onClick={() => window.print()}
                variant="outline"
                size="sm"
              >
                <Printer className="h-4 w-4 mr-2" />
                Drucken
              </Button>
              <Button
                onClick={async () => {
                  const t = toast.loading('🔥 Firmen werden bewertet (max 10)...');
                  try {
                    const token = localStorage.getItem('token');
                    const res = await fetch(
                      `${process.env.REACT_APP_BACKEND_URL}/api/companies/bulk-score?status=APPROVED&limit=10`,
                      { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
                    );
                    const data = await res.json();
                    toast.dismiss(t);
                    if (!res.ok) throw new Error(data.detail || 'Fehler');
                    toast.success(`${data.processed} bewertet, ${data.remaining} verbleibend`);
                    if (onRefresh) onRefresh();
                  } catch (e) {
                    toast.dismiss(t);
                    toast.error('Bewertung fehlgeschlagen');
                  }
                }}
                variant="outline"
                size="sm"
                data-testid="bulk-score-btn"
                className="border-orange-400 text-orange-700 hover:bg-orange-50"
              >
                🔥 Alle bewerten
              </Button>

              {currentUser && (currentUser.role === 'admin' || currentUser.role === 'sub_admin') && (
                <Button
                  onClick={async () => {
                    if (window.confirm(`Alle ${approvedCompanies.length} genehmigte Firmen löschen?`)) {
                      const companyIds = approvedCompanies.map(c => c.id);
                      await onDelete(companyIds);
                    }
                  }}
                  variant="destructive"
                  size="sm"
                >
                  🗑️ Alle löschen ({approvedCompanies.length})
                </Button>
              )}
            </div>
          </div>
          <div className="mt-3">
            <Input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="🔍 Suchen (Name, Stadt, Telefon, E-Mail)..."
              data-testid="approved-search-input"
              className="max-w-md"
            />
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="all">
                Alle Genehmigten ({normalApproved.length})
              </TabsTrigger>
              <TabsTrigger value="recall">
                <Phone className="h-4 w-4 mr-1" />
                Zum Anrufen ({recallList.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="all">
              <div className="space-y-4">
                {normalApproved.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    Alle Unternehmen sind zum Anrufen markiert
                  </div>
                ) : (
                  normalApproved.map((company) => (
              <Card key={company.id} className="border-green-200 relative">
                <CardContent className="pt-6">
                  {currentUser && (currentUser.role === 'admin' || currentUser.role === 'sub_admin') && (
                    <button
                      onClick={async () => {
                        if (window.confirm(`Firma "${company.title}" wirklich löschen?`)) {
                          await onDelete([company.id]);
                        }
                      }}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white w-7 h-7 rounded-full text-sm transition-colors flex items-center justify-center"
                      title="Löschen"
                    >
                      🗑️
                    </button>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      {/* 1. Tam şirket adı + Hot Lead Score */}
                      <h3 className="font-bold text-lg text-slate-900 mb-3 flex items-center gap-2 flex-wrap">
                        {company.title}
                        {company.lead_score && (
                          <span
                            data-testid={`lead-score-badge-${company.id}`}
                            title={company.lead_score_reason || ''}
                            className={`text-xs font-bold px-2 py-0.5 rounded-full border ${
                              company.lead_score >= 4
                                ? 'bg-red-100 text-red-700 border-red-300'
                                : company.lead_score >= 3
                                ? 'bg-orange-100 text-orange-700 border-orange-300'
                                : 'bg-slate-100 text-slate-600 border-slate-300'
                            }`}
                          >
                            🔥 {company.lead_score}/5
                          </span>
                        )}
                      </h3>
                      
                      <div className="space-y-2 text-sm">
                        {/* 2. Tıklanabilir adres - sadece şehir ismi göster */}
                        {company.address && (
                          <a 
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(company.address)}`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline block font-medium"
                            title={company.address}
                          >
                            📍 {(() => {
                              // Extract city name from address
                              const match = company.address.match(/\d{5}\s+([A-Za-zäöüßÄÖÜ\s-]+)/);
                              if (match) return match[1].trim();
                              
                              // Fallback: Try to find known city names
                              const cities = ['Hamburg', 'Berlin', 'München', 'Bremen', 'Hannover', 'Kiel', 'Lübeck', 'Schwerin', 'Dresden', 'Leipzig', 'Köln', 'Frankfurt', 'Stuttgart', 'Düsseldorf', 'Dortmund'];
                              const foundCity = cities.find(city => company.address.includes(city));
                              return foundCity || company.address.substring(0, 30) + '...';
                            })()}
                          </a>
                        )}
                        
                        {/* 3. Şirket açıklaması (eğer varsa) */}
                        {company.ai_analysis && (
                          <div className="text-slate-700 text-xs bg-slate-50 p-2 rounded">
                            {company.ai_analysis.substring(0, 150)}...
                          </div>
                        )}
                        
                        {/* 4. Merkezi telefon numarası */}
                        {company.phone && (
                          <div className="text-slate-900 font-medium">
                            📞 Zentrale: {company.phone}
                          </div>
                        )}
                        
                        {/* Website link */}
                        <a 
                          href={company.website?.startsWith('http') ? company.website : `https://${company.website}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline block text-xs"
                        >
                          🌐 Website öffnen
                        </a>
                      </div>
                    </div>

                    <div className="bg-blue-50 p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-sm sm:text-base text-slate-900 mb-2">👤 Ansprechpartner</h4>
                      <div className="space-y-1 text-xs sm:text-sm">
                        {company.contact_name ? (
                          <>
                            <div className="font-bold text-slate-900">{company.contact_name}</div>
                            {company.contact_title && (
                              <div className="text-blue-700 font-medium">💼 {company.contact_title}</div>
                            )}
                            <div className="text-slate-600">📧 {company.contact_email || 'Keine E-Mail'}</div>
                            {company.contact_linkedin && (
                              <a 
                                href={company.contact_linkedin} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline block"
                              >
                                🔗 LinkedIn Profil
                              </a>
                            )}
                          </>
                        ) : (
                          <div className="text-slate-500 italic">
                            ⚠️ Apollo konnte keine Kontaktperson finden
                          </div>
                        )}

                        {/* Impressum scraped data */}
                        {(company.impressum_emails?.length > 0 || company.impressum_phones?.length > 0) && (
                          <div className="mt-2 pt-2 border-t border-blue-200">
                            <div className="text-xs font-semibold text-slate-700 mb-1">📋 Aus Impressum:</div>
                            {company.impressum_emails?.slice(0, 3).map((em, i) => (
                              <div key={`em-${i}`} className="text-slate-700 text-xs">
                                <a href={`mailto:${em}`} className="text-blue-600 hover:underline">📧 {em}</a>
                              </div>
                            ))}
                            {company.impressum_phones?.slice(0, 3).map((ph, i) => (
                              <div key={`ph-${i}`} className="text-slate-700 text-xs">
                                <a href={`tel:${ph}`} className="text-blue-600 hover:underline">📞 {ph}</a>
                              </div>
                            ))}
                          </div>
                        )}

                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2 text-xs h-7"
                          data-testid={`scrape-impressum-${company.id}`}
                          onClick={async () => {
                            const t = toast.loading('Impressum wird gescrapt...');
                            try {
                              const token = localStorage.getItem('token');
                              const res = await fetch(
                                `${process.env.REACT_APP_BACKEND_URL}/api/companies/${company.id}/scrape-impressum`,
                                {
                                  method: 'POST',
                                  headers: { Authorization: `Bearer ${token}` },
                                }
                              );
                              const data = await res.json();
                              toast.dismiss(t);
                              if (!res.ok) {
                                toast.error(data.detail || 'Impressum nicht gefunden');
                                return;
                              }
                              const em = data.emails?.length || 0;
                              const ph = data.phones?.length || 0;
                              toast.success(`${em} E-Mail, ${ph} Telefon gefunden`);
                              if (onRefresh) onRefresh();
                            } catch (e) {
                              toast.dismiss(t);
                              toast.error('Scrape-Fehler');
                            }
                          }}
                        >
                          🔎 Impressum scrapen
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-1 text-xs h-7 ml-1 border-purple-400 text-purple-700 hover:bg-purple-50"
                          data-testid={`find-dm-${company.id}`}
                          onClick={async () => {
                            const t = toast.loading('🎯 Entscheider werden gesucht...');
                            try {
                              const token = localStorage.getItem('token');
                              const res = await fetch(
                                `${process.env.REACT_APP_BACKEND_URL}/api/companies/${company.id}/find-decision-makers`,
                                {
                                  method: 'POST',
                                  headers: { Authorization: `Bearer ${token}` },
                                }
                              );
                              const data = await res.json();
                              toast.dismiss(t);
                              if (!res.ok) {
                                toast.error(data.detail || 'Fehler');
                                return;
                              }
                              toast.success(`${data.count || 0} Entscheider gefunden`);
                              if (onRefresh) onRefresh();
                            } catch (e) {
                              toast.dismiss(t);
                              toast.error('Suche fehlgeschlagen');
                            }
                          }}
                        >
                          🎯 Entscheider finden
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-1 text-xs h-7 ml-1 border-orange-400 text-orange-700 hover:bg-orange-50"
                          data-testid={`score-lead-${company.id}`}
                          onClick={async () => {
                            const t = toast.loading('🔥 Website wird analysiert...');
                            try {
                              const token = localStorage.getItem('token');
                              const res = await fetch(
                                `${process.env.REACT_APP_BACKEND_URL}/api/companies/${company.id}/score-lead`,
                                { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
                              );
                              const data = await res.json();
                              toast.dismiss(t);
                              if (!res.ok) {
                                toast.error(data.detail || 'Analyse fehlgeschlagen');
                                return;
                              }
                              toast.success(`🔥 Lead-Score: ${data.lead_score}/5`, { description: data.lead_score_reason });
                              if (onRefresh) onRefresh();
                            } catch (e) {
                              toast.dismiss(t);
                              toast.error('Analyse fehlgeschlagen');
                            }
                          }}
                        >
                          🔥 Lead-Score
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-1 text-xs h-7 ml-1 border-green-400 text-green-700 hover:bg-green-50"
                          data-testid={`directory-phone-${company.id}`}
                          onClick={async () => {
                            const t = toast.loading('📞 Branchenbuch wird durchsucht...');
                            try {
                              const token = localStorage.getItem('token');
                              const res = await fetch(
                                `${process.env.REACT_APP_BACKEND_URL}/api/companies/${company.id}/find-phone-directory`,
                                { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
                              );
                              const data = await res.json();
                              toast.dismiss(t);
                              if (!res.ok) {
                                toast.error(data.detail || 'Keine Nummer gefunden');
                                return;
                              }
                              toast.success(`📞 ${data.phones.length} Nummer(n) gefunden (11880.com)`);
                              if (onRefresh) onRefresh();
                            } catch (e) {
                              toast.dismiss(t);
                              toast.error('Suche fehlgeschlagen');
                            }
                          }}
                        >
                          📞 Telefonbuch
                        </Button>

                        {/* Hiring signals & company hints */}
                        {(company.lead_score_reason || company.company_size_hint || company.fleet_hint || company.directory_phones?.length > 0) && (
                          <div className="mt-3 pt-2 border-t border-orange-200 space-y-1">
                            {company.lead_score_reason && (
                              <div className="text-xs text-orange-800">🔥 {company.lead_score_reason}</div>
                            )}
                            {company.career_url && (
                              <a href={company.career_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline block">
                                💼 Karriere-Seite öffnen
                              </a>
                            )}
                            {company.company_size_hint && (
                              <div className="text-xs text-slate-700">👥 {company.company_size_hint}</div>
                            )}
                            {company.fleet_hint && (
                              <div className="text-xs text-slate-700">🚛 {company.fleet_hint}</div>
                            )}
                            {company.founded_hint && (
                              <div className="text-xs text-slate-700">📅 Gegründet {company.founded_hint}</div>
                            )}
                            {company.directory_phones?.slice(0, 3).map((ph, i) => (
                              <div key={`dp-${i}`} className="text-xs">
                                <a href={`tel:${ph}`} className="text-green-700 hover:underline">📞 {ph} (Branchenbuch)</a>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Decision makers list */}
                        {company.decision_makers?.length > 0 && (
                          <div className="mt-3 pt-2 border-t border-purple-200">
                            <div className="text-xs font-semibold text-purple-800 mb-2">
                              🎯 Entscheider ({company.decision_makers.length}):
                            </div>
                            <div className="space-y-2">
                              {company.decision_makers.slice(0, 5).map((dm, i) => (
                                <div key={`dm-${i}`} className="bg-purple-50 p-2 rounded text-xs">
                                  <div className="font-semibold text-slate-900">{dm.name}</div>
                                  {dm.position && (
                                    <div className="text-purple-700">{dm.position}</div>
                                  )}
                                  <a
                                    href={dm.linkedin_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline text-xs block mt-1"
                                  >
                                    🔗 LinkedIn-Profil öffnen
                                  </a>
                                  {dm.email_guesses?.length > 0 && (
                                    <div className="mt-1">
                                      <div className="text-slate-500 text-xs">Mögliche E-Mails:</div>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {dm.email_guesses.slice(0, 4).map((em, j) => (
                                          <a
                                            key={`em-${j}`}
                                            href={`mailto:${em}`}
                                            className="text-xs text-blue-600 hover:underline bg-white px-1 py-0.5 rounded border"
                                          >
                                            {em}
                                          </a>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="bg-amber-50 p-3 sm:p-4 rounded-lg">
                      <h4 className="font-semibold text-sm sm:text-base text-slate-900 mb-2">📞 Telefonnummern</h4>
                      <div className="space-y-1 text-xs sm:text-sm">
                        {company.contact_phone && (
                          <div className="font-medium text-green-700">
                            Direkt: {company.contact_phone}
                          </div>
                        )}
                        {company.phone && (
                          <div className="text-slate-700">
                            Zentrale: {company.phone}
                          </div>
                        )}
                        {!company.contact_phone && !company.phone && (
                          <div className="text-slate-500 italic">
                            ⚠️ Keine Telefonnummer verfügbar
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                    {/* Recall Checkbox & Notes Button */}
                    <div className="flex items-center justify-between gap-4 pb-3 border-b border-gray-100">
                      <div className="flex-1">
                        <label className="flex items-center gap-2 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={company.needs_recall || false}
                            onChange={() => handleRecallToggle(company.id, company.needs_recall)}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                          />
                          <span className="text-sm font-medium text-slate-700 group-hover:text-blue-600">
                            📞 Erneut anrufen
                          </span>
                        </label>
                        {company.last_called_date && (
                          <p className="text-xs text-slate-500 ml-6 mt-1">
                            Zuletzt angerufen: {new Date(company.last_called_date).toLocaleDateString('de-DE')}
                          </p>
                        )}
                      </div>

                      <Button
                        onClick={() => openNotesModal(company)}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2"
                      >
                        <StickyNote className="h-4 w-4" />
                        Notiz hinzufügen
                      </Button>
                    </div>

                    {/* Show notes if exists */}
                    {company.notes && company.notes.length > 0 && (
                      <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                        <div className="flex items-start gap-2">
                          <StickyNote className="h-4 w-4 text-yellow-600 mt-0.5" />
                          <div className="flex-1 space-y-2">
                            <h5 className="font-semibold text-xs text-yellow-800 mb-2">Notizen ({company.notes.length}):</h5>
                            {Array.isArray(company.notes) ? (
                              [...company.notes].reverse().map((note, idx) => {
                                const originalIdx = company.notes.length - 1 - idx;
                                return (
                                <div key={originalIdx} className="border-l-2 border-yellow-400 pl-2 pb-2 mb-2">
                                  <p className="text-xs text-yellow-700">
                                    {new Date(note.created_at).toLocaleString('de-DE')} - {note.created_by}
                                  </p>
                                  {editingNoteIndex === `${company.id}-${originalIdx}` ? (
                                    <div className="mt-2">
                                      <Textarea
                                        value={editingNoteText}
                                        onChange={(e) => setEditingNoteText(e.target.value)}
                                        className="text-sm min-h-[80px]"
                                      />
                                      <div className="flex gap-2 mt-2">
                                        <Button
                                          size="sm"
                                          onClick={() => {
                                            handleUpdateNote(company.id, originalIdx, editingNoteText);
                                            setEditingNoteIndex(null);
                                          }}
                                        >
                                          Speichern
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            setEditingNoteIndex(null);
                                            setEditingNoteText('');
                                          }}
                                        >
                                          Abbrechen
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <p className="text-sm text-slate-700 whitespace-pre-wrap mt-1">{note.text}</p>
                                      <div className="flex gap-2 mt-2">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            setEditingNoteIndex(`${company.id}-${originalIdx}`);
                                            setEditingNoteText(note.text);
                                          }}
                                          className="text-xs"
                                        >
                                          ✏️ Bearbeiten
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="destructive"
                                          onClick={() => handleDeleteNote(company.id, originalIdx)}
                                          className="text-xs"
                                        >
                                          🗑️ Löschen
                                        </Button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              )})
                            ) : (
                              <p className="text-sm text-slate-700">{company.notes}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Detailed Analysis Button */}
                    <Button
                      onClick={() => fetchDetailedAnalysis(company.id)}
                      disabled={loadingAnalysis === company.id}
                      variant="outline"
                      className="w-full"
                    >
                      {loadingAnalysis === company.id ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Wird geladen...
                        </>
                      ) : (
                        <>
                          <FileText className="mr-2 h-4 w-4" />
                          Detaillierte Analyse {expandedId === company.id ? 'ausblenden' : 'anzeigen'}
                        </>
                      )}
                    </Button>

                    {expandedId === company.id && detailedAnalysis[company.id] && (
                      <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <h4 className="font-bold text-lg mb-3 text-blue-900">📊 Detaillierte Unternehmensanalyse</h4>
                        <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">
                          {detailedAnalysis[company.id]}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
                )}
              </div>
            </TabsContent>

            <TabsContent value="recall">
              <div className="space-y-4">
                {recallList.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Phone className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>Keine Unternehmen zum Anrufen markiert</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg border border-blue-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-blue-50 border-b border-blue-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Firma</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Zuletzt angerufen</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Nächster Anruf</th>
                            <th className="px-4 py-3 text-center text-sm font-semibold text-slate-900">Aktionen</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {recallList.map((company) => (
                            <tr key={company.id} className="hover:bg-blue-50/30 transition-colors">
                              <td className="px-4 py-3">
                                <div 
                                  className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer hover:underline"
                                  onClick={() => {
                                    setSelectedCompany(company);
                                    setViewCompanyModal(true);
                                  }}
                                >
                                  {company.title}
                                </div>
                                <div className="text-xs text-slate-500 mt-1">
                                  📞 {company.contact_phone || company.phone || 'Keine Telefonnummer'}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-600">
                                {company.last_called_date 
                                  ? new Date(company.last_called_date).toLocaleDateString('de-DE')
                                  : '-'}
                              </td>
                              <td className="px-4 py-3">
                                {editingDateCompanyId === company.id ? (
                                  <div className="flex items-center gap-2">
                                    <Input
                                      type="text"
                                      placeholder="TT.MM.JJJJ"
                                      value={tempDate}
                                      onChange={(e) => setTempDate(e.target.value)}
                                      className="text-sm w-32"
                                    />
                                    <Button
                                      size="sm"
                                      variant="default"
                                      onClick={async () => {
                                        // Parse date format DD.MM.YYYY to YYYY-MM-DD
                                        const parts = tempDate.split('.');
                                        if (parts.length === 3) {
                                          const isoDate = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                          
                                          try {
                                            const response = await fetch(
                                              `${process.env.REACT_APP_BACKEND_URL}/api/companies/${company.id}/recall`,
                                              {
                                                method: 'PATCH',
                                                headers: {
                                                  'Content-Type': 'application/json',
                                                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                                                },
                                                body: JSON.stringify({ 
                                                  needs_recall: true,
                                                  next_recall_date: isoDate
                                                })
                                              }
                                            );

                                            if (response.ok) {
                                              toast.success('✅ Termin gespeichert');
                                              setEditingDateCompanyId(null);
                                              setTempDate('');
                                              onRefresh && onRefresh();
                                            } else {
                                              toast.error('❌ Fehler beim Speichern');
                                            }
                                          } catch (error) {
                                            console.error('Update next recall date failed:', error);
                                            toast.error('❌ Fehler beim Speichern');
                                          }
                                        } else {
                                          toast.error('❌ Ungültiges Format. Bitte TT.MM.JJJJ verwenden');
                                        }
                                      }}
                                    >
                                      Speichern
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setEditingDateCompanyId(null);
                                        setTempDate('');
                                      }}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <div 
                                    className="text-sm text-slate-700 cursor-pointer hover:text-blue-600 flex items-center gap-2"
                                    onClick={() => {
                                      setEditingDateCompanyId(company.id);
                                      if (company.next_recall_date) {
                                        const date = new Date(company.next_recall_date);
                                        const formatted = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
                                        setTempDate(formatted);
                                      } else {
                                        setTempDate('');
                                      }
                                    }}
                                  >
                                    {company.next_recall_date 
                                      ? new Date(company.next_recall_date).toLocaleDateString('de-DE')
                                      : 'Datum setzen'}
                                    <Calendar className="h-3 w-3" />
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-center gap-2">
                                  <Button
                                    onClick={() => openNotesModal(company)}
                                    variant="outline"
                                    size="sm"
                                    className="text-xs"
                                  >
                                    <StickyNote className="h-3 w-3 mr-1" />
                                    Notiz
                                  </Button>
                                  <Button
                                    onClick={() => handleRecallToggle(company.id, company.needs_recall)}
                                    variant="outline"
                                    size="sm"
                                    className="text-xs"
                                  >
                                    ✓ Erledigt
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Notes Modal */}
      <Dialog open={notesModalOpen} onOpenChange={setNotesModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="h-5 w-5" />
              Notizen für {currentNotesCompany?.title}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <Textarea
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
              placeholder="Fügen Sie hier Ihre Notizen hinzu..."
              className="min-h-[150px] resize-none"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNotesModalOpen(false)}
              disabled={savingNotes}
            >
              Abbrechen
            </Button>
            <Button
              onClick={saveNotes}
              disabled={savingNotes}
            >
              {savingNotes ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Speichern...
                </>
              ) : (
                'Speichern'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Company Details Modal */}
      <Dialog open={viewCompanyModal} onOpenChange={setViewCompanyModal}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              Firma Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedCompany && (
            <div className="space-y-4">
              {/* Company Name */}
              <div>
                <h3 className="font-bold text-xl text-slate-900">{selectedCompany.title}</h3>
                {selectedCompany.address && (
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedCompany.address)}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-sm mt-1 inline-block"
                  >
                    📍 {selectedCompany.address}
                  </a>
                )}
              </div>

              {/* Quick Info */}
              <div className="grid grid-cols-2 gap-4">
                {/* Phone Numbers */}
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <h4 className="font-semibold text-sm text-slate-900 mb-2">📞 Telefonnummern</h4>
                  {selectedCompany.contact_phone && (
                    <div className="text-sm font-medium text-green-700 mb-1">
                      Direkt: {selectedCompany.contact_phone}
                    </div>
                  )}
                  {selectedCompany.phone && (
                    <div className="text-sm text-slate-700">
                      Zentrale: {selectedCompany.phone}
                    </div>
                  )}
                  {!selectedCompany.contact_phone && !selectedCompany.phone && (
                    <div className="text-sm text-slate-500 italic">
                      Keine Telefonnummer verfügbar
                    </div>
                  )}
                </div>

                {/* Contact Person */}
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-sm text-slate-900 mb-2">👤 Ansprechpartner</h4>
                  {selectedCompany.contact_name ? (
                    <>
                      <div className="text-sm font-bold text-slate-900">{selectedCompany.contact_name}</div>
                      {selectedCompany.contact_title && (
                        <div className="text-xs text-blue-700 font-medium mt-1">
                          💼 {selectedCompany.contact_title}
                        </div>
                      )}
                      {selectedCompany.contact_email && (
                        <div className="text-xs text-slate-600 mt-1">
                          📧 {selectedCompany.contact_email}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-sm text-slate-500 italic">
                      Keine Kontaktperson gefunden
                    </div>
                  )}
                </div>
              </div>

              {/* Website */}
              {selectedCompany.website && (
                <div>
                  <a 
                    href={selectedCompany.website?.startsWith('http') ? selectedCompany.website : `https://${selectedCompany.website}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-sm"
                  >
                    🌐 Website öffnen
                  </a>
                </div>
              )}

              {/* AI Analysis */}
              {selectedCompany.ai_analysis && (
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <h4 className="font-semibold text-sm text-slate-900 mb-2">🤖 KI-Analyse</h4>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">
                    {selectedCompany.ai_analysis}
                  </p>
                </div>
              )}

              {/* Notes */}
              {selectedCompany.notes && selectedCompany.notes.length > 0 && (
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <h4 className="font-semibold text-sm text-yellow-800 mb-3">
                    📝 Notizen ({selectedCompany.notes.length})
                  </h4>
                  <div className="space-y-3">
                    {Array.isArray(selectedCompany.notes) ? (
                      [...selectedCompany.notes].reverse().map((note, idx) => {
                        const originalIdx = selectedCompany.notes.length - 1 - idx;
                        return (
                        <div key={originalIdx} className="border-l-2 border-yellow-400 pl-3 pb-3">
                          <p className="text-xs text-yellow-700">
                            {new Date(note.created_at).toLocaleString('de-DE')} - {note.created_by}
                          </p>
                          {editingNoteIndex === `modal-${originalIdx}` ? (
                            <div className="mt-2">
                              <Textarea
                                value={editingNoteText}
                                onChange={(e) => setEditingNoteText(e.target.value)}
                                className="text-sm min-h-[80px]"
                              />
                              <div className="flex gap-2 mt-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleUpdateNote(selectedCompany.id, originalIdx, editingNoteText)}
                                >
                                  Speichern
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingNoteIndex(null);
                                    setEditingNoteText('');
                                  }}
                                >
                                  Abbrechen
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{note.text}</p>
                              <div className="flex gap-2 mt-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingNoteIndex(`modal-${originalIdx}`);
                                    setEditingNoteText(note.text);
                                  }}
                                  className="text-xs"
                                >
                                  ✏️ Bearbeiten
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleDeleteNote(selectedCompany.id, originalIdx)}
                                  className="text-xs"
                                >
                                  🗑️ Löschen
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      )})
                    ) : (
                      <p className="text-sm text-slate-700">{selectedCompany.notes}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Recall Info */}
              {selectedCompany.needs_recall && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-sm text-blue-800 mb-2">📞 Anruf-Informationen</h4>
                  <div className="space-y-1 text-sm">
                    {selectedCompany.last_called_date && (
                      <div>
                        <span className="font-medium">Zuletzt angerufen:</span>{' '}
                        {new Date(selectedCompany.last_called_date).toLocaleDateString('de-DE')}
                      </div>
                    )}
                    {selectedCompany.next_recall_date && (
                      <div>
                        <span className="font-medium">Nächster Anruf:</span>{' '}
                        {new Date(selectedCompany.next_recall_date).toLocaleDateString('de-DE')}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button
              onClick={() => {
                if (selectedCompany) {
                  fetchDetailedAnalysis(selectedCompany.id);
                }
              }}
              disabled={loadingAnalysis === selectedCompany?.id}
              variant="outline"
            >
              {loadingAnalysis === selectedCompany?.id ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird geladen...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  {detailedAnalysis[selectedCompany?.id] && expandedId === selectedCompany?.id
                    ? 'Analyse ausblenden'
                    : 'Detaillierte Analyse'}
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setViewCompanyModal(false)}
            >
              Schließen
            </Button>
          </DialogFooter>
          
          {/* Detailed Analysis Display */}
          {selectedCompany && expandedId === selectedCompany.id && detailedAnalysis[selectedCompany.id] && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-bold text-lg mb-3 text-blue-900">📊 Detaillierte Unternehmensanalyse</h4>
              <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">
                {detailedAnalysis[selectedCompany.id]}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprovedList;
